import { useMemo } from "react";
import { type CustomerMembershipDto, useMyMemberships } from "../../api/hooks/useMemberships";
import { isActiveSubscription, useMySubscriptions } from "../../api/hooks/useStrategySubscriptions";
import { type TradingStrategyDto, useTradingStrategies } from "../../api/hooks/useTradingStrategies";

/** Where access to some strategies comes from: the customer's own subscriptions, or one membership. */
export interface FollowableSource {
  key: string;
  /** Null for the strategies the customer subscribes to on their own. */
  membership: CustomerMembershipDto | null;
  strategyIds: ReadonlySet<string>;
}

export const SUBSCRIBED_SOURCE = "subscribed";

/** Every strategy whose signals the customer receives, by name, and where each comes from. */
export function useFollowableStrategies() {
  const subscriptions = useMySubscriptions();
  const memberships = useMyMemberships();
  const tradingStrategies = useTradingStrategies();

  const followable = useMemo(() => {
    const strategiesById = new Map((tradingStrategies.data ?? []).map((strategy) => [strategy.id, strategy]));
    const found = new Map<string, TradingStrategyDto>();
    const sources: FollowableSource[] = [];

    const subscribed = (subscriptions.data ?? []).filter(
      (subscription) => subscription.status === "Active" && isActiveSubscription(subscription),
    );
    if (subscribed.length > 0) {
      for (const subscription of subscribed) {
        found.set(
          subscription.strategy.id,
          strategiesById.get(subscription.strategy.id) ?? subscription.strategy,
        );
      }
      sources.push({
        key: SUBSCRIBED_SOURCE,
        membership: null,
        strategyIds: new Set(subscribed.map((subscription) => subscription.strategy.id)),
      });
    }

    for (const membership of memberships.data ?? []) {
      const included = membership.tradingStrategyIds.filter((id) => strategiesById.has(id));
      if (included.length === 0) {
        continue;
      }
      for (const id of included) {
        found.set(id, strategiesById.get(id)!);
      }
      sources.push({ key: membership.id, membership, strategyIds: new Set(included) });
    }

    const strategies = [...found.values()].sort(
      (a, b) => a.publicName.localeCompare(b.publicName) || (a.direction ?? 0) - (b.direction ?? 0),
    );

    return { strategies, sources };
  }, [subscriptions.data, memberships.data, tradingStrategies.data]);

  return {
    ...followable,
    isLoading: subscriptions.isLoading || memberships.isLoading || tradingStrategies.isLoading,
    error: subscriptions.error ?? memberships.error ?? tradingStrategies.error,
  };
}
